import React, { useState } from "react"

// ---------------------------------------------------------

import { getAllEventsData } from "@lib/api"
import { fetchRecommended } from "@lib/fetchRecommended"
import FlexibleData from "@lib/flexibleData"
import RenderBlocks from "@lib/renderBlocks"

// ---------------------------------------------------------

import Button from "@components/button"
import Card from "@components/card"
import Layout from "../../layout"
import NewsletterSignUp from "@components/forms/newsletter-signup"
import PingPong from "@components/pingpong"
import RecommendedReading from "@components/recommended-reading"

// ---------------------------------------------------------

import {
  button_container,
  cards_container,
  cards_reduced_container,
  header,
  no_results
} from "./styles.module.scss"

// ---------------------------------------------------------

const LIMIT = 12

// ---------------------------------------------------------

export async function getStaticProps(context) {
  const preview = process.env.NODE_ENV === "development" || context.draftMode || false
  const previewEnv = context?.previewData?.environment || null
  const pageData = (await FlexibleData.fetch("events", preview, previewEnv)) ?? []

  const allEvents = await getAllEventsData(preview)
  const eventsAndWebinarsData = allEvents.normalizedEventsAndWebinars ?? []
  const externalEventsData = allEvents.externalEvents ?? []

  const recommended = pageData.showRecommended
    ? await fetchRecommended(pageData?.recommendedCollection?.items, "events", "blogs", preview)
    : null

  return {
    props: {
      pageData,
      eventsAndWebinarsData,
      externalEventsData,
      recommended,
      preview
    }
  }
}

const EventsIndexPage = (props) => {
  const { pageData, eventsAndWebinarsData, externalEventsData, recommended, preview } = props

  const [endEventPost, setEndEventPost] = useState(LIMIT)
  const filteredEventsPosts = eventsAndWebinarsData.slice(0, endEventPost)

  const pageModules = pageData?.modulesCollection?.items ?? []
  let pageTitle = "Upcoming Events & Webinars"
  let excerpt = "Find out about upcoming events that Amperity is hosting or partipating in."

  const eventsAndWebinarsAreFew =
    eventsAndWebinarsData.length > 0 && eventsAndWebinarsData.length < 3

  return (
    <Layout preview={preview} pageTitle={pageTitle} excerpt={excerpt}>
      {pageModules && RenderBlocks(pageModules)}

      <>
        <h1 className={header}>{pageTitle}</h1>

        {eventsAndWebinarsData.length < 1 && (
          <p className={no_results}>More Amperity events coming soon!</p>
        )}

        {/* Amperity hosted events */}
        {eventsAndWebinarsData.length > 0 && (
          <div className={eventsAndWebinarsAreFew ? cards_reduced_container : cards_container}>
            {filteredEventsPosts.map((event, index) => {
              let eventLabel = event.resourceType !== "Webinar" ? "Event" : "Webinar"
              let buttonUrl = null
              if (event.resourceType !== "Webinar") {
                buttonUrl = `/events/${encodeURIComponent(event.slug)}`
              } else {
                buttonUrl = `/resources/webinars/${encodeURIComponent(event.slug)}`
              }
              return (
                <div key={index}>
                  <Card
                    theme="hover-grid"
                    label={eventLabel}
                    title={event.title}
                    image={{
                      alt: event.featuredImage.description,
                      src: event.featuredImage.url,
                      height: event.featuredImage.height,
                      width: event.featuredImage.width
                    }}
                    startDate={event.startDate}
                    endDate={event.endDate}
                    timeZoneOverride={event.timeZoneOverride}
                    location={event.location}
                    description={event.excerpt}
                    buttonUrl={buttonUrl}
                    tagCollection={event.tags}
                  />
                </div>
              )
            })}
          </div>
        )}
        {endEventPost < eventsAndWebinarsData.length - 1 && (
          <div className={button_container}>
            <Button theme="dark" onClick={() => setEndEventPost(endEventPost + LIMIT)}>
              Show more
            </Button>
          </div>
        )}
      </>

      {/* External events */}
      {externalEventsData.length > 0 && (
        <>
          <h2 className={header}>Connect with us at...</h2>

          <div className={cards_container}>
            {externalEventsData.map((event, index) => {
              let eventLabel = "Event"
              return (
                <div key={index}>
                  <Card
                    key={index}
                    theme="hover-grid"
                    label={eventLabel}
                    title={event.title}
                    image={{
                      alt: event.featuredImage.description,
                      src: event.featuredImage.url,
                      height: event.featuredImage.height,
                      width: event.featuredImage.width
                    }}
                    startDate={event.startDate}
                    endDate={event.endDate}
                    description={event.excerpt || event.excerptLong}
                    buttonUrl={event.externalEventLink}
                    buttonLabel={event.buttonLabel}
                  ></Card>
                </div>
              )
            })}
          </div>
        </>
      )}

      <PingPong
        imageOnLeft
        backgroundColor="off-black"
        image={{
          src: "https://amperity.imgix.net/1M1CJN4NxglEj8glQDY7f0/ab3367dcb9d30751e72b09962369ee8d/Ampersend_Hero.jpg?auto=format,compress&w=1620&h=1013",
          alt: "Hand holding mobile device displaying Ampersend",
          width: 1600,
          height: 1080
        }}
      >
        <span className="label">Newsletter</span>
        <h2>Be the first to know about upcoming Amperity events</h2>
        <p>
          Submit your email below to join thousands of marketing, analytics, and data professionals
          who receive our monthly newsletter.
        </p>
        <NewsletterSignUp theme="gray-background" formId="2028" />
      </PingPong>
      {recommended && (
        <RecommendedReading
          recommended={recommended}
          title="Explore Blogs"
          buttonLabel="View all blogs"
          buttonUrl="/blog"
        ></RecommendedReading>
      )}
    </Layout>
  )
}

export default EventsIndexPage
